/* eslint-disable react/no-children-prop */
import { useState } from 'react'
import {
  Datagrid,
  DatagridBody,
  List,
  Toolbar,
  useTranslate,
  Button as ButtonRA,
} from 'react-admin'
import {
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  makeStyles,
  Table,
  TableHead,
  TableCell,
  TableFooter,
  TableRow,
} from '@material-ui/core'
import { BaseWidget } from './base'
import { ChevronLeft, ChevronRight } from '@mui/icons-material'
import { Boolean, Pill, PillLink, SimpleText } from './cell_content_components'
import { Link } from 'react-router-dom'
import config from '../../config'

const useStyles = makeStyles(() => ({
  gridWidgetList: {
    padding: '0 16px',
  },
  rowPill: {
    height: '40px',
  },
  cellTextValue: {
    minWidth: '6.5em',
    border: 'none',
  },
  cellWidget: {
    borderBottom: 'none',
    padding: 0,
    maxWidth: '100px',
  },
  tableWidgetFooter: {
    position: 'absolute',
    bottom: '2.5rem',
    left: 0,
    right: 0,
  },
}))

/**
 * Returns an array that have inside the given array splitted on the number of elements passed by into size param.
 *
 * @param { Array } array
 * @param { Number } size
 *
 * @returns Array[]
 */
const chunkArray = (array, size) => {
  const result = []
  for (let i = 0; i < array.length; i += size) {
    let chunk = array.slice(i, i + size)
    result.push(chunk)
  }

  return result
}

/**
 * This function is in charge of knowing how to render the rows.
 *
 * @todo: move the collapsible row logic here.
 *
 * @param {
 *   rawRows: Object[] | undefined,
 *   cellData: {
 *    quantity: Number | undefined,
 *    type: "text" | "pill"
 *    link: String
 *   },
 *   isWidgetRow: boolean = false
 * } props
 *
 * @returns TableRow || TableRow[]
 */
const RowsRenderer = ({ rawRows, cellData, isWidgetRow = false }) => {
  if (!rawRows || rawRows.length === 0)
    return (
      <TableRow>
        <CellRender
          isWidgetCell={isWidgetRow}
          value="There are no records to show"
          type={'text'}
        />
      </TableRow>
    )

  let { quantity, ...cellProps } = cellData

  return chunkArray(rawRows, quantity).map((chunkRows) => {
    let filters = {}

    return (
      <TableRow>
        {chunkRows.map((row) =>
          Object.keys(row).map((columnName) => {
            let link
            if (
              columnName.toLowerCase().indexOf('_filter') !== -1 ||
              columnName.toLowerCase().indexOf('_hidden') !== -1
            ) {
              filters[
                columnName.replace('_filter', '').replace('_hidden', '')
              ] = row[columnName]
              link = cellProps.link
              // If is a pill it doesn't matter if is not the filter column we need to add the link
            } else if ('pill' === cellProps.type) link = cellProps.link

            return (
              <CellRender
                {...cellProps}
                maxLength={isWidgetRow ? 10 : 30}
                isWidgetCell={isWidgetRow}
                columnName={columnName}
                value={row[columnName]}
                link={link}
                detail_update_resource={cellProps.detail_update_resource}
                filter={filters}
              />
            )
          })
        )}
      </TableRow>
    )
  })
}

/**
 * This function will render the right component based on type into a TableCell
 *
 * @param { Object } props
 * @param { String } props.columnName
 * @param { Any } props.value
 * @param { Number } props.maxLength
 * @param { String || undefined } props.link Link to the list view
 * @param { Object || undefined } props.filter The filters to be used to update resource and also for list link
 * @param { String || undefined } props.detail_update_resource The resource name to update it will use the first existing value between filter.detail_id || filter.id || filter.code
 * @param { Function || undefined } props.onChange
 * @param { Boolean } props.isWidgetCell
 * @param { 'text' || 'pill' } props.type
 *
 * @returns TableCell | null
 */
const CellRender = ({
  columnName = '',
  value,
  maxLength,
  link,
  filter,
  detail_update_resource,
  onChange,
  isWidgetCell = false,
  type = 'text',
}) => {
  const classes = useStyles()
  const strToCheck = columnName.toLowerCase()
  if (strToCheck.indexOf('hidden') !== -1) return null
  let cellValueComponent
  if ('pill' === type) {
    if (link)
      cellValueComponent = (
        <PillLink link={link} value={value} filter={filter} />
      )
    else cellValueComponent = <Pill value={value} />
  } else {
    if (strToCheck.indexOf('is_') !== -1) {
      cellValueComponent = (
        <Boolean
          value={value}
          disabled={isWidgetCell}
          fieldName={columnName}
          onChange={onChange}
          detail_update_resource={
            !isWidgetCell && detail_update_resource
              ? `${config.API_V1}/${detail_update_resource}/${
                  filter.detail_id || filter.id || filter.code
                }`
              : null
          }
        />
      )
    } else if (link)
      cellValueComponent = (
        <PillLink link={link} value={value} filter={filter} />
      )
    else
      cellValueComponent = (
        <SimpleText
          className={classes.cellTextValue}
          value={value}
          maxLength={maxLength}
        />
      )
  }

  return (
    <TableCell
      className={isWidgetCell || 'pill' === type ? classes.cellWidget : null}
      key={`${columnName}-cell`}
      children={cellValueComponent}
    />
  )
}

/**
 * @todo: let's update react admin in order to allow header customization of grids
 *
 * @param { record: Object } props
 *
 * @returns TableHead
 */
const ProcessListDGHeader = ({ record }) => (
  <TableHead>
    {record.list[0].rows.map((row) => (
      <TableRow>
        {Object.keys(row).map((columnName) => (
          <TableCell key={columnName}>
            <h1>{columnName}</h1>
          </TableCell>
        ))}
      </TableRow>
    ))}
  </TableHead>
)

/**
 * @param { record: Object } props
 *
 * @returns TableRow[]
 */
const ProcessListDGRow = ({ record, refresh }) => {
  const [currentRows] = record.list

  return (
    <RowsRenderer
      rawRows={currentRows.rows}
      cellData={{
        quantity: 'pill' === currentRows.render_type ? 4 : 1,
        link: currentRows.list_link,
        type: currentRows.render_type,
        detail_update_resource: currentRows.detail_update_resource,
      }}
    />
  )
}
/**
 * @param { * } props
 *
 * @returns DatagridBody
 */
const ProcessListDGBody = (props) => (
  <DatagridBody {...props} row={<ProcessListDGRow />} />
)

/**
 * @param { * } props
 *
 * @returns Datagrid
 */
const ProcessListDG = (props) => (
  <Datagrid header={<ProcessListDGHeader />} body={<ProcessListDGBody />} />
)

/**
 * @param {
 *    loaded: Boolean,
 *    data: Object[],
 *    page: Number,
 *    total: Number,
 *    perPage: Number,
 *    setPage: Function
 * } props
 *
 * @returns Toolbar
 */
const ProcessListPagination = ({
  loaded,
  data,
  page,
  total,
  perPage,
  setPage,
}) => {
  const translate = useTranslate()
  if (!loaded) return <div></div>
  const nbPages = Math.ceil(total / perPage) || 1

  return (
    nbPages > 1 && (
      <Toolbar>
        {page > 1 && (
          <Button
            color="primary"
            key="prev"
            icon={ChevronLeft}
            onClick={() => setPage(page - 1)}
          >
            {translate('ra.navigation.prev')}
          </Button>
        )}
        {page !== nbPages && (
          <Button
            color="primary"
            key="next"
            icon={ChevronRight}
            onClick={() => setPage(page + 1)}
            labelPosition="before"
          >
            {translate('ra.navigation.next')}
          </Button>
        )}
      </Toolbar>
    )
  )
}

/**
 * @param {
 *   showProcess: String,
 *   process: Object,
 *   onClose: Function,
 *   resource: Object
 * } props
 *
 * @returns Dialog
 */
export const WidgetModal = ({ showProcess, process, onClose, resource }) => (
  <Dialog
    open={showProcess === process.name}
    onClose={onClose}
    maxWidth="sm"
    fullWidth={true}
  >
    <DialogTitle>{process.name}</DialogTitle>
    <DialogContent>
      <List
        basePath=""
        resource={`modal:${resource}`}
        bulkActionButtons={false}
        exporter={false}
        actions={null}
        title={null}
        perPage={'pill' === process.render_type ? 24 : 10} // @todo: find a way to use the filter paginated.
        filter={{
          process_name: process.name,
        }}
        pagination={<ProcessListPagination />}
      >
        <ProcessListDG />
      </List>
    </DialogContent>
    <DialogActions>
      <Button
        variant="contained"
        color="secondary"
        onClick={onClose}
        disableElevation
      >
        Fermer
      </Button>
    </DialogActions>
  </Dialog>
)

/**
 * @param { * } props
 *
 * @returns Grid
 */
export const WidgetList = (props) => {
  const [showProcess, setShowProcess] = useState(null)
  const onClose = () => {
    setShowProcess(null)
  }
  const classes = useStyles()

  return (
    <Grid container spacing={1} className={classes.gridWidgetList}>
      {props.processes.map((process) => (
        <>
          <BaseWidget props={{ ...props, ...process, minHeight: '350px' }}>
            <Box
              display="flex"
              paddingX={1}
              width={330}
              justifyContent="space-between"
            >
              <Table
                sx={{ minWidth: 650 }}
                size="small"
                aria-label="widget table"
              >
                {process.rows && process.rows.length > 0 ? (
                  <>
                    <RowsRenderer
                      rawRows={process.rows}
                      isWidgetRow={true}
                      cellData={{
                        quantity: 'pill' === process.render_type ? 3 : 1,
                        link: process.list_link,
                        type: process.render_type,
                      }}
                    />
                    <TableFooter className={classes.tableWidgetFooter}>
                      <ButtonRA
                        variant="contained"
                        color="primary"
                        component={Link}
                        to={{
                          pathname: '/tickets',
                          search: `filter=${JSON.stringify({
                            process_id: process.id,
                          })}`,
                        }}
                        disableElevation
                      >
                        Consulter
                      </ButtonRA>
                    </TableFooter>
                  </>
                ) : (
                  <RowsRenderer isWidgetRow={true} />
                )}
              </Table>
            </Box>
          </BaseWidget>
          <WidgetModal
            showProcess={showProcess}
            onClose={onClose}
            process={process}
            resource={props.resource}
          />
        </>
      ))}
    </Grid>
  )
}
